import { useState, useEffect, useCallback } from 'react'
import { writingAssistantService } from '../../services/writingAssistantService'
import type { WritingAssistantListItem } from '../../types/writing'

export function useWritingAssistants(crisisId?: string) {
  const [writingAssistants, setWritingAssistants] = useState<WritingAssistantListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchWritingAssistants = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await writingAssistantService.getWritingAssistants(crisisId)
      setWritingAssistants(response.data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch writing assistants'
      setError(errorMessage)
      console.error('Error fetching writing assistants:', err)
    } finally {
      setIsLoading(false)
    }
  }, [crisisId])

  const updateUnreadyWritingAssistants = async (
    translatingIds?: string[],
    forceUpdateIds?: string[]
  ) => {
    try {
      const response = await writingAssistantService.getWritingAssistants(crisisId)
      const latestWritingAssistants = response.data

      // Update only the writing assistants that are currently unready or being translated
      // Also update writing assistants that were being translated (to catch completed translations)
      setWritingAssistants(prevWritingAssistants =>
        prevWritingAssistants.map(prevWA => {
          const latestWA = latestWritingAssistants.find(wa => wa.id === prevWA.id)
          // Update if this writing assistant was unready, is being translated, or needs force update
          if (
            latestWA &&
            (prevWA.status === 'queued' ||
              prevWA.status === 'processing' ||
              (translatingIds && translatingIds.includes(prevWA.id)) ||
              (forceUpdateIds && forceUpdateIds.includes(prevWA.id)))
          ) {
            return latestWA
          }
          return prevWA
        })
      )
    } catch (err) {
      console.error('Error updating unready writing assistants:', err)
    }
  }

  useEffect(() => {
    fetchWritingAssistants()
  }, [crisisId, fetchWritingAssistants])

  // Poll for lightweight changes (ids, status, updatedAt) and only fetch full
  // items when an item is new or updated by another user.
  useEffect(() => {
    if (!crisisId) return undefined

    let cancelled = false
    const inFlightFetches = new Set<string>()

    const fetchFullItem = async (id: string) => {
      if (inFlightFetches.has(id)) return
      inFlightFetches.add(id)
      try {
        const resp = await writingAssistantService.getWritingAssistant(id)
        const fullItem = resp.data
        if (cancelled) return

        setWritingAssistants(prev => {
          const exists = prev.some(p => p.id === fullItem.id)
          if (exists) {
            return prev.map(p => (p.id === fullItem.id ? fullItem : p))
          }
          // New items should appear at the top
          return [fullItem, ...prev]
        })
      } catch (err) {
        console.error('Error fetching full writing assistant item:', err)
      } finally {
        inFlightFetches.delete(id)
      }
    }

    const poll = async () => {
      try {
        const response = await writingAssistantService.getWritingAssistants(crisisId)
        const latest = response.data

        setWritingAssistants(prev => {
          // Build maps for quick lookup
          const prevMap = new Map(prev.map(p => [p.id, p]))
          const latestMap = new Map(latest.map(l => [l.id, l]))

          // Detect deletions: remove items that no longer exist
          const kept = prev.filter(p => latestMap.has(p.id))

          // For each latest item, detect new or updated
          latest.forEach(latestItem => {
            const prevItem = prevMap.get(latestItem.id)
            const latestUpdated = new Date(latestItem.updatedAt).getTime()
            const prevUpdated = prevItem ? new Date(prevItem.updatedAt).getTime() : 0

            // New item
            if (!prevItem) {
              fetchFullItem(latestItem.id)
              return
            }

            // Updated item (status or updatedAt changed)
            if (latestItem.status !== prevItem.status || latestUpdated > prevUpdated) {
              fetchFullItem(latestItem.id)
              return
            }
          })

          // Keep the current prev items (we'll replace them when full fetches arrive)
          return kept
        })
      } catch (err) {
        console.error('Error polling writing assistants lightweight list:', err)
      }
    }

    // Start polling every 5 seconds
    const intervalId = setInterval(poll, 5000)

    // Run an initial poll shortly after mount to catch remote changes quickly
    const initTimeout = setTimeout(poll, 1000)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      clearTimeout(initTimeout)
    }
  }, [crisisId])

  return {
    writingAssistants,
    isLoading,
    error,
    refetch: fetchWritingAssistants,
    updateUnreadyWritingAssistants,
  }
}
